import { useEffect, useState } from 'react';
import type { TabKey } from './sourceTabMetadata';
import {
  loadLatestSourceStats,
  type SourceStatsBySource,
  type SourceStatsFileKey,
} from '../../../../services/sourceStatsService';

type TabStatsConfig = {
  file: SourceStatsFileKey;
  column: string;
};

const TAB_STATS_CONFIG: Partial<Record<TabKey, TabStatsConfig>> = {
  GDA: { file: 'gdas', column: 'gdas' },
  DISEASES: { file: 'gdas', column: 'diseases' },
  GENES: { file: 'gdas', column: 'genes' },
  VDA: { file: 'vdas', column: 'vdas' },
  VARIANTS: { file: 'vdas', column: 'variants' },
  CHEMICALS: { file: 'gdas', column: 'Gchemicals' },
};

function resolveTabStatsConfig(
  tab: string,
  options?: { view?: string; currentTab?: string },
): TabStatsConfig | undefined {
  if (tab === 'CHEMICALS' && options?.view === 'CHEMICALS' && options.currentTab === 'VDA') {
    return { file: 'vdas', column: 'Vchemicals' };
  }

  return TAB_STATS_CONFIG[tab as TabKey];
}

async function loadStatsFile(fileKey: SourceStatsFileKey): Promise<SourceStatsBySource> {
  const stats = await loadLatestSourceStats();
  return fileKey === 'gdas' ? stats.gdas : stats.vdas;
}

export async function getSourceTabRecordCount(
  source: string,
  tab: string,
  options?: { view?: string; currentTab?: string },
): Promise<number | null> {
  const config = resolveTabStatsConfig(tab, options);
  if (!config) return null;

  const stats = await loadStatsFile(config.file);
  const sourceStats = stats[source];
  if (!sourceStats) return null;

  const count = sourceStats[config.column];
  return typeof count === 'number' ? count : null;
}

export function formatRecordCount(count: number): string {
  return count.toLocaleString('en-US');
}

export function useSourceTabStats(source: string, tabs: string[], view?: string, currentTab?: string) {
  const [counts, setCounts] = useState<Record<string, number | null>>({});

  useEffect(() => {
    let cancelled = false;

    const loadCounts = async () => {
      const entries = await Promise.all(
        tabs.map(async tab => {
          const count = await getSourceTabRecordCount(source, tab, { view, currentTab });
          return [tab, count] as const;
        }),
      );

      if (!cancelled) {
        setCounts(Object.fromEntries(entries));
      }
    };

    loadCounts().catch(() => {
      if (!cancelled) {
        setCounts({});
      }
    });

    return () => {
      cancelled = true;
    };
  }, [source, tabs.join('|'), view, currentTab]);

  return counts;
}
